// The server deduplicates reminder creation by id. A retry must reuse the
// original body so a lost response cannot create a second task.
import { createSaveDiagnostic, diagnosticMark, diagnosticHeaders } from "./saveDiagnostics.mjs";

const RETRY_DELAYS_MS = [1000, 3000, 6000, 12000];

export async function postScopedReminder(reminder, {
  request = fetch,
  pause = ms => new Promise(resolve => setTimeout(resolve, ms)),
  diagnostic = createSaveDiagnostic(),
} = {}) {
  const body = JSON.stringify(reminder);
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    diagnosticMark(diagnostic, "reminder_attempt_start", { attempt:attempt + 1 });
    try {
      const response = await request("/api/crm/reminders", {
        method:"POST", credentials:"same-origin",
        headers:{ "Content-Type":"application/json", Accept:"application/json", ...diagnosticHeaders(diagnostic, attempt + 1) },
        body,
      });
      diagnosticMark(diagnostic, "reminder_response_headers", { attempt:attempt + 1, status:response.status });
      const payload = await response.json().catch(() => null);
      diagnosticMark(diagnostic, "reminder_response_body", { attempt:attempt + 1, status:response.status });
      if (response.ok && payload?.ok === true) {
        diagnosticMark(diagnostic, "reminder_attempt_end", { attempt:attempt + 1, outcome:"confirmed" });
        return { response, payload, uncertain:false };
      }
      // An edge proxy can return an HTML 429 without reaching the CRM. A 5xx
      // or truncated success can also conceal a write that did succeed.
      const transient = response.status === 429 || response.status >= 500
        || (response.ok && !payload);
      diagnosticMark(diagnostic, "reminder_attempt_end", { attempt:attempt + 1, outcome:transient ? "uncertain" : "rejected" });
      if (!transient) return { response, payload, uncertain:false };
      if (attempt < RETRY_DELAYS_MS.length) {
        diagnosticMark(diagnostic, "retry_pause_start", { attempt:attempt + 1, pauseMs:RETRY_DELAYS_MS[attempt] });
        await pause(RETRY_DELAYS_MS[attempt]);
        diagnosticMark(diagnostic, "retry_pause_end", { attempt:attempt + 1, pauseMs:RETRY_DELAYS_MS[attempt] });
        continue;
      }
      return { response, payload, uncertain:true };
    } catch {
      diagnosticMark(diagnostic, "reminder_attempt_error", { attempt:attempt + 1, outcome:"error" });
      diagnosticMark(diagnostic, "reminder_attempt_end", { attempt:attempt + 1, outcome:"error" });
      if (attempt < RETRY_DELAYS_MS.length) {
        diagnosticMark(diagnostic, "retry_pause_start", { attempt:attempt + 1, pauseMs:RETRY_DELAYS_MS[attempt] });
        await pause(RETRY_DELAYS_MS[attempt]);
        diagnosticMark(diagnostic, "retry_pause_end", { attempt:attempt + 1, pauseMs:RETRY_DELAYS_MS[attempt] });
        continue;
      }
      return { uncertain:true };
    }
  }
}